import React, { FC, useEffect } from 'react';
import { RouteComponentProps, Link, useHistory, useLocation } from 'react-router-dom';
import { Input, Button, Row, Col, Card, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import './styles.less';
import {
  invitation,
  noBusinessProfile,
  notAuthorized,
  onboarding,
  passwordResetRequest,
} from '~/routes';
import { ChangeLanguage, Form, PrivacyPolicyFooter } from '~/components';
import { useLogin } from '~/hooks/auth';
import { LogoWithTextImg } from '~/img';
import { auth } from '~/api/axios';
import featuresWithDefaults from '~/featuresWithDefaults';

const { Title, Text } = Typography;

export const passwordMinLength = 8;

type DispatchResetReduxState = () => () => void;
export const useDispatchResetReduxState: DispatchResetReduxState = () => {
  const dispatch = useDispatch();

  return () => dispatch({ type: 'reset/state' });
};

export const useResetReduxState = (): void => {
  const dispatch = useDispatch();
  auth.deleteToken();

  useEffect(() => {
    dispatch({ type: 'reset/state' });
  }, [dispatch]);
};

const Login: FC = () => {
  const location = useLocation();
  const history = useHistory();

  const { login, isLoading } = useLogin({
    onSuccess() {
      const prevPagePath = (location.state as any)?.prevPagePath as string | undefined;
      history.push(prevPagePath || '/');
    },
    onNoBusinessProfile() {
      const prevPagePath = (location.state as any)?.prevPagePath as string | undefined;
      if (prevPagePath?.includes(`/${invitation}`)) {
        history.push(prevPagePath || '/');
      } else {
        history.push(`/${noBusinessProfile}`);
      }
    },
    onNotAuthorized() {
      const prevPagePath = (location.state as any)?.prevPagePath as string | undefined;
      if (prevPagePath?.includes(`/${invitation}`)) {
        history.push(prevPagePath || '/');
      } else {
        history.push(`/${notAuthorized}`);
      }
    },
  });

  const onFinish = async (values: any) => {
    const { email, password } = values;
    try {
      await login(email, password);
    } catch (error: any) {
      // Do nothing
    }
  };

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const signUpUrl = featuresWithDefaults?.signUpUrl;

  return (
    <>
      <ChangeLanguage />
      <div className="Login">
        <Row justify="center" align="middle" className="Onboarding-LogoRow">
          <Col>
            <LogoWithTextImg />
          </Col>
        </Row>
        <Row justify="center" align="top" className="Login-Row">
          <Col flex="0 1 400px">
            <Card className="Login-Card">
              <Title className="Login-Title" level={2}>
                {t('businessPortal').toUpperCase()}
              </Title>
              <Text type="secondary" className="Login-TextSignIn">
                {t('signInToYourAccount')}
              </Text>
              <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      type: 'email',
                    },
                  ]}
                >
                  <Input placeholder={t('email')} prefix={<UserOutlined />} />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      min: passwordMinLength,
                    },
                  ]}
                >
                  <Input.Password placeholder={t('password')} prefix={<LockOutlined />} />
                </Form.Item>
                <div className="Login-PasswordResetContainer">
                  <Link className="Login-LinkPasswordReset" to={`/${passwordResetRequest}`}>
                    {t('Forgot password?')}
                  </Link>
                </div>
                <Form.Item noStyle>
                  <Button type="primary" htmlType="submit" loading={isLoading}>
                    {t('login')}
                  </Button>
                </Form.Item>
              </Form>
              {signUpUrl ? (
                <a
                  className="Login-LinkSingUp"
                  href={signUpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('signUp')}
                </a>
              ) : (
                <Link className="Login-LinkSingUp" to={`/${onboarding}`}>
                  {t('signUp')}
                </Link>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      <Row gutter={[0, 16]} justify="center" align="middle">
        <Col>
          <PrivacyPolicyFooter />
        </Col>
      </Row>
    </>
  );
};

const LoginWithReset: FC<RouteComponentProps> = (props) => {
  useResetReduxState();
  return <Login {...props} />;
};

export default LoginWithReset;
